import { cn } from '@amore/ui/cn'
import { Button } from '@amore/ui/components/button'
import { Input } from '@amore/ui/components/input'
import { Textarea } from '@amore/ui/components/textarea'
import { ArrowLeft, BedDouble, Car, Check, Plus, X } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { newId } from '@/lib/format'
import { dietLabels } from '@/lib/labels'
import { useUpdate, useWedding } from '@/mock/store'
import type { Diet, Party, ReplyAnswer, WebReply } from '@/mock/types'
import { partyTitle } from '@/screens/guests/model'

type Row = Omit<ReplyAnswer, 'attendance'> & {
  attendance: ReplyAnswer['attendance'] | null
  /** On the invitation as "+ doprovod", or added by the guest. */
  companion: boolean
}

const diets = Object.keys(dietLabels) as Diet[]

/**
 * B2: the reply for one invitation. Each person: comes or not, and (when
 * asked) their diet; a companion gets a name. Then what the couple asks in
 * Nastavení: accommodation, transport, a message. Sending creates a web reply
 * the couple reviews in "Odpovědi z webu"; nothing changes the list directly.
 */
export function ReplyForm({
  party,
  onBack,
  onSent,
}: {
  party: Party
  onBack: () => void
  onSent: (reply: WebReply) => void
}) {
  const data = useWedding()
  const update = useUpdate()
  const { ask, companions, children } = data.guestSettings
  const [rows, setRows] = useState<Row[]>(() =>
    party.persons.map((person) => ({
      personId: person.id,
      firstName: person.firstName,
      lastName: person.lastName,
      attendance:
        person.attendance === 'confirmed' || person.attendance === 'declined'
          ? person.attendance
          : null,
      diets: person.diets,
      child: person.child,
      companion: Boolean(person.plusOne),
    })),
  )
  const [accommodation, setAccommodation] = useState(party.accommodation)
  const [transport, setTransport] = useState(party.transport)
  const [message, setMessage] = useState('')
  const [missing, setMissing] = useState(false)
  const parts = data.dayParts.filter((part) => party.parts.includes(part.id))
  const anyoneComing = rows.some((row) => row.attendance === 'confirmed')

  const setRow = (id: string, patch: Partial<Row>) => {
    setMissing(false)
    setRows((current) => current.map((row) => (row.personId === id ? { ...row, ...patch } : row)))
  }
  const addRow = (child: boolean) =>
    setRows((current) => [
      ...current,
      {
        personId: newId('guest'),
        firstName: '',
        lastName: '',
        attendance: 'confirmed',
        diets: [],
        child,
        companion: !child,
      },
    ])

  const send = () => {
    if (rows.some((row) => row.attendance === null)) {
      setMissing(true)
      return
    }
    const first = rows.find((row) => !row.companion && row.firstName)
    const reply: WebReply = {
      id: newId('reply'),
      partyId: party.id,
      name: first ? `${first.firstName} ${first.lastName}`.trim() : partyTitle(party),
      answers: rows.map(({ companion: _companion, ...row }) => {
        // A companion or child types one "Jméno a příjmení" field; split it for the list.
        const [firstName = '', ...rest] = row.lastName
          ? [row.firstName]
          : row.firstName.trim().split(/\s+/)
        return {
          ...row,
          firstName,
          lastName: row.lastName || rest.join(' '),
          attendance: row.attendance ?? 'declined',
        }
      }),
      accommodation: anyoneComing && accommodation,
      transport: anyoneComing && transport,
      message: message.trim(),
      receivedAt: new Date().toISOString().slice(0, 16),
      status: 'new',
    }
    update((current) => ({ ...current, webReplies: [reply, ...current.webReplies] }))
    onSent(reply)
  }

  return (
    <div className="space-y-8 pb-10">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 pt-8 text-sm font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft aria-hidden="true" className="size-4" />
        Nejste to vy?
      </button>

      <header className="space-y-3 text-center">
        <p className="text-xs font-semibold tracking-[0.24em] text-action uppercase">Pozvánka</p>
        <h1 className="font-heading text-4xl leading-tight">{partyTitle(party)}</h1>
        {parts.length ? (
          <ul
            aria-label="Na co vás zveme"
            className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-muted-foreground"
          >
            {parts.map((part) => (
              <li key={part.id}>
                <span className="text-foreground">{part.label}</span>
                {part.time ? ` ${part.time}` : ''}
              </li>
            ))}
          </ul>
        ) : null}
      </header>

      <section aria-label="Kdo přijde" className="space-y-3">
        {rows.map((row) => (
          <PersonCard
            key={row.personId}
            row={row}
            askDiets={ask.diets}
            removable={!party.persons.some((person) => person.id === row.personId)}
            missing={missing && row.attendance === null}
            onChange={(patch) => setRow(row.personId, patch)}
            onRemove={() =>
              setRows((current) => current.filter((item) => item.personId !== row.personId))
            }
          />
        ))}
        {companions === 'anyone' || children === 'welcome' ? (
          <div className="flex flex-wrap justify-center gap-2 pt-1">
            {companions === 'anyone' && !rows.some((row) => row.companion) ? (
              <Button size="sm" appearance="outline" tone="neutral" onClick={() => addRow(false)}>
                <Plus aria-hidden="true" className="size-3.5" />
                Přivedu doprovod
              </Button>
            ) : null}
            {children === 'welcome' ? (
              <Button size="sm" appearance="outline" tone="neutral" onClick={() => addRow(true)}>
                <Plus aria-hidden="true" className="size-3.5" />
                Přivedu dítě
              </Button>
            ) : null}
          </div>
        ) : null}
        {children === 'none' ? (
          <p className="text-center text-sm text-muted-foreground">
            Svatbu chystáme jen pro dospělé, děkujeme za pochopení.
          </p>
        ) : null}
      </section>

      {anyoneComing && (ask.accommodation || ask.transport) ? (
        <section className="space-y-3 text-center">
          <h2 className="font-semibold">Potřebujete něco zařídit?</h2>
          <div className="flex flex-wrap justify-center gap-2">
            {ask.accommodation ? (
              <Toggle
                icon={BedDouble}
                pressed={accommodation}
                onClick={() => setAccommodation(!accommodation)}
              >
                Ubytování
              </Toggle>
            ) : null}
            {ask.transport ? (
              <Toggle icon={Car} pressed={transport} onClick={() => setTransport(!transport)}>
                Odvoz
              </Toggle>
            ) : null}
          </div>
        </section>
      ) : null}

      {ask.message ? (
        <section className="space-y-3">
          <label htmlFor="reply-message" className="block text-center font-semibold">
            Vzkaz pro nás
          </label>
          <Textarea
            id="reply-message"
            value={message}
            maxLength={1000}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Na co se těšíte, na co nemáme zapomenout…"
            className="min-h-28 rounded-2xl bg-card text-base"
          />
        </section>
      ) : null}

      <div className="space-y-3">
        {missing ? (
          <p role="alert" className="text-center text-sm text-error-foreground">
            U každého prosím vyberte, jestli přijde.
          </p>
        ) : null}
        <Button onClick={send} className="h-12 w-full text-base">
          Odeslat odpověď
        </Button>
      </div>
    </div>
  )
}

/** One person: name (a companion types theirs), comes or not as two big buttons, then diet. */
function PersonCard({
  row,
  askDiets,
  removable,
  missing,
  onChange,
  onRemove,
}: {
  row: Row
  askDiets: boolean
  removable: boolean
  missing: boolean
  onChange: (patch: Partial<Row>) => void
  onRemove: () => void
}) {
  const named = Boolean(row.firstName || row.lastName)
  const editableName = row.companion || removable
  return (
    <div
      className={cn(
        'space-y-4 rounded-[1.5rem] border bg-card p-5',
        missing ? 'border-error-foreground/60' : 'border-border',
      )}
    >
      <div className="flex items-center gap-2">
        {editableName ? (
          <Input
            value={row.firstName}
            onChange={(event) => onChange({ firstName: event.target.value })}
            placeholder={row.child ? 'Jméno a příjmení dítěte' : 'Jméno a příjmení doprovodu'}
            aria-label={row.child ? 'Jméno dítěte' : 'Jméno doprovodu'}
            className="h-11 flex-1 rounded-full text-base"
          />
        ) : (
          <p className="flex-1 text-lg font-semibold">
            {named ? `${row.firstName} ${row.lastName}`.trim() : 'Doprovod'}
          </p>
        )}
        {removable ? (
          <Button
            appearance="ghost"
            tone="neutral"
            size="icon"
            onClick={onRemove}
            aria-label="Odebrat"
            className="size-11"
          >
            <X aria-hidden="true" className="size-4" />
          </Button>
        ) : null}
      </div>

      <fieldset className="grid grid-cols-2 gap-2">
        <legend className="sr-only">Přijde?</legend>
        <Choice
          selected={row.attendance === 'confirmed'}
          tone="yes"
          onClick={() => onChange({ attendance: 'confirmed' })}
        >
          <Check aria-hidden="true" className="size-4" strokeWidth={2.5} />
          Přijde
        </Choice>
        <Choice
          selected={row.attendance === 'declined'}
          tone="no"
          onClick={() => onChange({ attendance: 'declined' })}
        >
          <X aria-hidden="true" className="size-4" strokeWidth={2.5} />
          Nepřijde
        </Choice>
      </fieldset>

      {askDiets && row.attendance === 'confirmed' ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Strava</p>
          <div className="flex flex-wrap gap-2">
            {diets.map((diet) => {
              const pressed = row.diets.includes(diet)
              return (
                <Button
                  key={diet}
                  size="sm"
                  appearance="outline"
                  tone="neutral"
                  aria-pressed={pressed}
                  onClick={() =>
                    onChange({
                      diets: pressed
                        ? row.diets.filter((item) => item !== diet)
                        : [...row.diets, diet],
                    })
                  }
                >
                  {dietLabels[diet]}
                </Button>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function Choice({
  selected,
  tone,
  onClick,
  children,
}: {
  selected: boolean
  tone: 'yes' | 'no'
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'inline-flex h-12 items-center justify-center gap-2 rounded-full border text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        selected
          ? tone === 'yes'
            ? 'border-transparent bg-success-foreground text-background'
            : 'border-transparent bg-danger-foreground text-white'
          : 'border-border bg-card text-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  )
}

function Toggle({
  icon: Icon,
  pressed,
  onClick,
  children,
}: {
  icon: typeof Car
  pressed: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <Button appearance="outline" tone="neutral" aria-pressed={pressed} onClick={onClick}>
      <Icon aria-hidden="true" className="size-4" />
      {children}
    </Button>
  )
}
